import { createRouter, createWebHistory } from 'vue-router';
import { can } from '@/composables/usePerms';
import { setTitleFromRoute } from '@/composables/usePageTitle';
import HomeView from '../views/HomeView.vue';
import LoginView from '../views/LoginView.vue';
import TaskDetail from '../views/TaskDetailView.vue';
import TaskEmailView from '@/views/TaskEmailView.vue';
import TestView from '@/views/TestView.vue';


import TasksListView from '../views/TasksListView.vue';
import TasksCriticalListView from '../views/TasksCriticalListView.vue';
import TasksTransmitterRelatedListView from '../views/TasksTransmitterRelatedListView.vue';
import TasksTodayListView from '../views/TasksTodayListView.vue';
import TasksTomorrowListView from '../views/TasksTomorrowListView.vue';
import TasksUnfinishedListView from '../views/TasksUnfinishedListView.vue';
import TasksInProgressListView from '../views/TasksInProgressListView.vue';
import TasksAgreedListView from '../views/TasksAgreedListView.vue';
import TasksOpenListView from '../views/TasksOpenListView.vue';
import TasksFinishedListView from '../views/TasksFinishedListView.vue';
import TasksOverdueListView from '../views/TasksOverdueListView.vue';
import TasksEstimatedOverdueListView from '../views/TasksEstimatedOverdueListView.vue';
import TasksMaintenanceListView from '../views/TasksMaintenanceListView.vue';
import TasksOrderListView from '../views/TasksOrderListView.vue';
import TasksInstallationListView from '../views/TasksInstallationListView.vue';
import StockItemsView from '@/views/StockItemsView.vue';
import RealestatesListView from '@/views/RealestatesListView.vue';
import RealestateDetailView from '@/views/RealestateDetailView.vue';
import ProjectsListView from '@/views/ProjectsListView.vue';
import ProjectDetailView from '@/views/ProjectDetailView.vue';
import ObjectsListView from '@/views/ObjectsListView.vue';
import ObjectDetailView from '@/views/ObjectDetailView.vue';
import NotificationsView from '@/views/NotificationsView.vue';
import CalendarView from '@/views/CalendarView.vue';
import SimsSignalsView from '@/views/SimsSignalsView.vue';
import WorkloadView from '@/views/WorkloadView.vue';
import TaskActTemplate from '@/views/TaskActTemplate.vue';
import ScanQRCode from '@/views/ScanQRCode.vue';
import NfcScanView from '@/views/NfcScanView.vue';
import { canUseNfc } from '@/utils/nfcAccess';
import TaskCreateView from '../views/TaskCreateView.vue';
import TasksGroupedListView from '@/views/TasksGroupedListView.vue';
import OnCallView from '@/views/OnCallView.vue';
import ProfileView from '@/views/ProfileView.vue';
import MaintenanceLogView from '@/views/MaintenanceLogView.vue';
import MapsView from '@/views/MapsView.vue';

const routes = [
    {
        path: '/',
        name: 'Home',
        component: HomeView,
        meta: { requiresAuth: true }
    },
    {
        path: '/login',
        name: 'login',
        component: LoginView,
        meta: { titleKey: 'login.title' }
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPasswordView.vue'),
        meta: { titleKey: 'password.forgotTitle', bare: true }
    },
    {
        // Linked from the reset e-mail (APP_PASSWORD_RESET_URL in the API).
        // Public and with no logged-in redirect, so the link also works on a
        // device that is still signed in.
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/ResetPasswordView.vue'),
        meta: { titleKey: 'password.resetTitle', bare: true }
    },
    {
        path: '/forbidden',
        name: 'Forbidden',
        component: () => import('@/views/ForbiddenView.vue'),
        meta: { titleKey: 'forbidden.title' }
    },
    {
        path: '/tasks/:taskId(\\d+)',
        name: 'TaskDetail',
        component: TaskDetail,
        meta: { requiresAuth: true, titleKey: 'taskViews.all' }
    },
    {
        path: '/test',
        name: 'Test',
        component: TestView,
        meta: { requiresAuth: true }
    },
    {
        path: '/tasks',
        name: 'TasksList',
        component: TasksListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.all' }
    },
    {
        // Blank form for a template: GET the structure, POST to create an act.
        path: '/tasks/:id(\\d+)/act/:template_id(\\d+)',
        name: 'TaskActTemplate',
        component: TaskActTemplate,
        meta: { requiresAuth: true, titleKey: 'taskViews.all' }
    },
    {
        // An already-saved act. A template with template_type_id 0 can hold several.
        path: '/tasks/:id(\\d+)/acts/:act_id(\\d+)',
        name: 'TaskAct',
        component: TaskActTemplate,
        meta: { requiresAuth: true, titleKey: 'taskViews.all' }
    },
    {
        path: '/tasks/:taskId(\\d+)/emails/:emailId(\\d+)',
        name: 'TaskEmail',
        component: TaskEmailView,
        meta: { requiresAuth: true, titleKey: 'taskDetail.sentEmails' }
    },
    {
        path: '/tasks-critical',
        name: 'TasksCritical',
        component: TasksCriticalListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.critical' }
    },
    {
        path: '/tasks-transmitter-related',
        name: 'TasksTransmitterRelated',
        component: TasksTransmitterRelatedListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.transmitterRelated' }
    },

    {
        path: '/tasks-overdue',
        name: 'TasksOverdue',
        component: TasksOverdueListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.overdue' }
    },
    {
        path: '/tasks-estimated-overdue',
        name: 'TasksEstimatedOverdue',
        component: TasksEstimatedOverdueListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.estimatedOverdue' }
    },
    {
        path: '/tasks-maintenance',
        name: 'TasksMaintenance',
        component: TasksMaintenanceListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.maintenance' }
    },
    {
        path: '/tasks-maintenance-grouped',
        name: 'TasksMaintenanceGrouped',
        component: TasksGroupedListView,
        meta: { requiresAuth: true, titleKey: 'home.maintenanceGrouped' },
        props: {
            priorityId: 1,
            targetRouteName: 'TasksUnfinished',
        },
    },
    {
        path: '/tasks-order',
        name: 'TasksOrder',
        component: TasksOrderListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.order' }
    },
    {
        path: '/tasks-order-grouped',
        name: 'TasksOrderGrouped',
        component: TasksGroupedListView,
        meta: { requiresAuth: true, titleKey: 'home.orderGrouped' },
        props: {
            priorityId: 2,
            targetRouteName: 'TasksUnfinished',
        },
    },
    {
        path: '/tasks-installation',
        name: 'TasksInstallation',
        component: TasksInstallationListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.installation' }
    },
    {
        path: '/tasks-installation-grouped',
        name: 'TasksInstallationGrouped',
        component: TasksGroupedListView,
        meta: { requiresAuth: true, titleKey: 'home.installationGrouped' },
        props: {
            priorityId: 3,
            targetRouteName: 'TasksUnfinished',
        },
    },
    {
        path: '/tasks-today',
        name: 'TasksToday',
        component: TasksTodayListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.today' }
    },
    {
        path: '/tasks-tomorrow',
        name: 'TasksTomorrow',
        component: TasksTomorrowListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.tomorrow' }
    },
    {
        path: '/tasks-unfinished',
        name: 'TasksUnfinished',
        component: TasksUnfinishedListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.unfinished' }
    },
    {
        path: '/tasks-unfinished-grouped',
        name: 'TasksUnfinishedGrouped',
        component: TasksGroupedListView,
        meta: { requiresAuth: true, titleKey: 'home.unfinishedGrouped' },
        props: {
            targetRouteName: 'TasksUnfinished',
        },
    },
    {
        path: '/tasks-inprogress',
        name: 'TasksInProgress',
        component: TasksInProgressListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.inProgress' }
    },
    {
        path: '/tasks-agreed',
        name: 'TasksAgreed',
        component: TasksAgreedListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.agreed' }
    },
    {
        path: '/tasks-open',
        name: 'TasksOpen',
        component: TasksOpenListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.open' }
    },
    {
        path: '/tasks-finished',
        name: 'TasksFinished',
        component: TasksFinishedListView,
        meta: { requiresAuth: true, titleKey: 'taskViews.finished' }
    },
    {
        path: '/stock-items',
        name: 'StockItems',
        component: StockItemsView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppWarehouse', titleKey: 'nav.warehouse' }
    },
    {
        path: '/realestates',
        name: 'RealestatesListView',
        component: RealestatesListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppRealestates', titleKey: 'nav.buildings' }
    },
    {
        path: '/realestates/:realestateId(\\d+)',
        name: 'RealestateDetail',
        component: RealestateDetailView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppRealestates', titleKey: 'nav.buildings' }
    },
    {
        path: '/realestates/:realestateId(\\d+)/tasks',
        name: 'RealestateTasks',
        component: TasksListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppRealestates', titleKey: 'taskViews.all' }
    },
    {
        path: '/realestates/:realestateId(\\d+)/maintenance-log',
        name: 'RealestateMaintenanceLog',
        component: MaintenanceLogView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppRealestates', titleKey: 'maintenanceLog.title', entity: 'realestates', idParam: 'realestateId' }
    },
    {
        path: '/projects',
        name: 'ProjectsListView',
        component: ProjectsListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'nav.projects' }
    },
    {
        path: '/projects/:projectId(\\d+)',
        name: 'ProjectDetail',
        component: ProjectDetailView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'nav.projects' }
    },
    {
        path: '/projects/:projectId(\\d+)/tasks',
        name: 'ProjectTasks',
        component: TasksListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'taskViews.all' }
    },
    {
        path: '/projects/:projectId(\\d+)/objects',
        name: 'ProjectObjects',
        component: ObjectsListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'nav.devices' }
    },
    {
        path: '/projects/:projectId(\\d+)/maintenance-log',
        name: 'ProjectMaintenanceLog',
        component: MaintenanceLogView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'maintenanceLog.title', entity: 'projects', idParam: 'projectId' }
    },
    {
        path: '/profile',
        name: 'ProfileView',
        component: ProfileView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProfile', titleKey: 'nav.profile' }
    },
    {
        path: '/notifications',
        name: 'NotificationsView',
        component: NotificationsView,
        meta: { requiresAuth: true, titleKey: 'notifications.title' }
    },
    {
        path: '/objects',
        name: 'ObjectsListView',
        component: ObjectsListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppObjects', titleKey: 'nav.devices' }
    },
    {
        path: '/objects/:objectId(\\d+)',
        name: 'ObjectsDetail',
        component: ObjectDetailView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppObjects', titleKey: 'nav.devices' }
    },
    {
        path: '/objects/:objectId(\\d+)/tasks',
        name: 'ObjectTasks',
        component: TasksListView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppObjects', titleKey: 'taskViews.all' }
    },
    {
        path: '/objects/:objectId(\\d+)/maintenance-log',
        name: 'ObjectMaintenanceLog',
        component: MaintenanceLogView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppObjects', titleKey: 'maintenanceLog.title', entity: 'objects', idParam: 'objectId' }
    },
    {
        path: '/tasks/:taskId(\\d+)/sims-signals',
        name: 'SimsSignals',
        component: SimsSignalsView,
        meta: { requiresAuth: true, titleKey: 'taskDetail.simsSignals', bare: true }
    },
    {
        path: '/calendar',
        name: 'CalendarView',
        component: CalendarView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppCalendar', titleKey: 'nav.calendar' }
    },
    {
        path: '/workload/:yearMonth?',
        name: 'WorkloadView',
        component: WorkloadView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppWorkload', titleKey: 'nav.workload' },
        props: true
    },
    {
        path: '/scan',
        name: 'ScanQRCode',
        component: ScanQRCode,
        meta: { requiresAuth: true, needPerm: 'Priv_AppObjects', titleKey: 'scan.title' },
        props: true
    },
    {
        path: '/nfc',
        name: 'NfcScan',
        component: NfcScanView,
        meta: { requiresAuth: true, titleKey: 'nfc.title' },
        // Not a Priv_App* permission, so needPerm cannot express it.
        beforeEnter: () => (canUseNfc() ? true : { name: 'Forbidden' })
    },
    {
        path: '/projects/:projectId(\\d+)/new-task',
        name: 'TaskCreateView',
        component: TaskCreateView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppProjects', titleKey: 'taskCreate.title' },
        props: true
    },
    {
        path: '/on-call',
        name: 'OnCall',
        component: OnCallView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppCalendar', titleKey: 'onCall.title' }
    },
    {
        path: '/maps',
        name: 'MapsView',
        component: MapsView,
        meta: { requiresAuth: true, needPerm: 'Priv_AppMaps', titleKey: 'nav.maps' }
    },
    // Catch-all. Without it an unmatched address mounts nothing and the user is
    // left on a page with no content and no way back. Public like /forbidden.
    // Vue Router ranks by specificity, so this cannot shadow a real route.
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/NotFoundView.vue'),
        meta: { titleKey: 'notFound.title' }
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        // Back/forward -> restore last scroll
        if (savedPosition) return savedPosition;
        // Normal navigation -> go to top
        return { left: 0, top: 0 };
    }
});

router.beforeEach((to, from, next) => {
    const isLoggedIn = !!localStorage.getItem('token');
    to.meta.internalNavigation = true;

    // auth gate
    if (to.meta.requiresAuth && !isLoggedIn) {
        return next({ path: '/login', query: { redirect: to.fullPath } });
    }

    // already logged in -> no need to see login
    if (to.path === '/login' && isLoggedIn) {
        const redirect = to.query.redirect;
        if (typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//') && !redirect.startsWith('/login')) {
            return next(redirect);
        }
        return next('/');
    }

    // permission gate (single required perm)
    const required = to.meta?.needPerm;
    if (required && !can(required)) {
        return next({ name: 'Forbidden' });
    }

    next();
});

// Records the path visited at each browser-history position so back-navigation
// can skip over entries. The browser exposes history.length but not the entries
// themselves, so we track them ourselves. Memory cost is one short string per
// navigation (session-scoped, cleared on reload) — negligible.
const navHistory = new Map();

// Returns a negative delta for router.go() that lands on the most recent tracked
// history entry NOT belonging to `taskPath` (i.e. not `/tasks/{id}` nor any
// `/tasks/{id}/...` sub-page), or null if no such entry is tracked.
export function backDeltaSkippingPath(taskPath) {
    const current = window.history.state?.position ?? 0;
    for (let pos = current - 1; pos >= 0; pos--) {
        const path = navHistory.get(pos);
        if (path === undefined) continue;
        if (path !== taskPath && !path.startsWith(taskPath + '/')) {
            return pos - current;
        }
    }
    return null;
}

// Set the document title from the destination route. Static/list pages resolve
// their localized `meta.titleKey`; `/` and unmatched routes fall back to the
// base title. Detail views (`/page/id`) override this with their item's name
// once loaded. This also resets any stale item name when leaving a detail page.
router.afterEach((to) => {
    navHistory.set(window.history.state?.position ?? 0, to.path);
    setTitleFromRoute(to);
});


export default router;
